<template>
  <main class="container">
    <h1 class="page-title" data-testid="page-title">
      When Would You Like Your <br />
      Broadband Installed?
    </h1>

    <BackButton class="back-link" />

    <InfoBox class="mt-0">
      <template v-slot>
        <p>
          If you choose ASAP, your broadband will be scheduled to start on your
          start date or as soon as possible.
        </p>

        <p>
          Move In Date:
          <strong class="text-dark-blue">{{
            minInstallationDate | FormatDate
          }}</strong>
        </p>
      </template>
    </InfoBox>

    <form
      class="form broadband-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <FormError
          v-if="$v.broadbandUrgent.$error && !$v.broadbandUrgent.required"
          data-testid="broadband-urgent-error"
          msg="You must specify broadband installation urgency"
        />
        <div class="broadband-form__options-wrapper">
          <div class="installation-option" data-testid="broadband-asap">
            <input
              type="radio"
              name="broadbandUrgent"
              id="broadbandUrgent1"
              data-testid="broadband-asap-input"
              value="true"
              v-model="broadbandUrgent"
            />
            <label
              for="broadbandUrgent1"
              class="installation-option__information"
            >
              <span class="installation-option__title">ASAP</span>
            </label>
          </div>
          <div class="installation-option" data-testid="broadband-later">
            <input
              type="radio"
              name="broadbandUrgent"
              id="broadbandUrgent2"
              data-testid="broadband-later-input"
              value="false"
              v-model="broadbandUrgent"
            />
            <label for="broadbandUrgent2" class="product__information">
              <span class="installation-option__title">Later</span>
            </label>
          </div>
        </div>

        <div v-show="!broadbandUrgent" class="broadband-date">
          <div
            class="form-item"
            :class="{ '--has-error': $v.broadbandDate.$error }"
          >
            <label class="form__label" for="installationDate">
              <small class="form__hint">
                Select your earliest preferred date, weekends may not be
                available.
              </small>
              <FormError
                v-if="$v.broadbandDate.$error && !$v.broadbandDate.required"
                data-testid="broadband-date-error"
                msg="You must select a date for later installation"
              />
              <FormError
                v-if="
                  $v.broadbandDate.$error &&
                    $v.broadbandDate.required &&
                    !$v.broadbandDate.minValue
                "
                data-testid="broadband-date-min-value-error"
                msg="Installation date cannot be before tenancy start"
              />
            </label>
            <div class="form__date-input">
              <input
                class="form__input"
                type="date"
                id="installationDate"
                data-testid="installation-date"
                name="installationDate"
                :min="minInstallationDate"
                v-model.trim="broadbandDate"
                ref="broadbandStartDatePickerField"
                @click="showDatePicker"
              />
              <CalendarIcon
                class="form__date-icon"
                width="20px"
                height="18px"
              />
            </div>
          </div>
        </div>
      </fieldset>

      <button
        class="btn btn--big form__submit"
        data-testid="next-button"
        type="submit"
        @click="trackSubmitBtn"
      >
        Next
      </button>
    </form>
  </main>
</template>

<script>
import Pikaday from "pikaday";
import { required, requiredIf } from "vuelidate/lib/validators";
import FormatDate from "@/filters/FormatDate";
import FormError from "@/components/forms/FormError.vue";
import CalendarIcon from "@/assets/svg/icon-calendar.svg";
import InfoBox from "@/components/InfoBox";

export default {
  name: "BroadbandDate",
  data() {
    return {
      broadbandStartDatePicker: undefined,
    };
  },
  computed: {
    broadbandUrgent: {
      get() {
        return this.$store.state.houseInformation.broadbandUrgent;
      },
      set(value) {
        value = value === "true";
        this.$store.commit("SET_BROADBAND_URGENCY", value);
      },
    },
    broadbandDate: {
      get() {
        return this.$store.state.houseInformation.broadbandDate;
      },
      set(value) {
        this.$store.commit("SET_BROADBAND_DATE", value);
      },
    },
    minInstallationDate() {
      return this.$store.state.houseInformation.startDate;
    },
  },
  methods: {
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$router.push("/full-summary");
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Next Broadband",
        "Create Account",
        "Confirm broadband install date",
      );
    },
    showDatePicker(event) {
      if (event) {
        event.target.showPicker();
      }
    },
  },
  mounted() {
    if (!this.checkDateInput()) {
      this.broadbandStartDatePicker = new Pikaday({
        field: this.$refs.broadbandStartDatePickerField,
        minDate: new Date(this.minInstallationDate),
        defaultDate: new Date(this.minInstallationDate),
      });
    } else if (!this.broadbandDate) {
      this.broadbandDate = this.minInstallationDate;
    }
  },
  destroyed() {
    if (!this.checkDateInput()) {
      this.broadbandStartDatePicker.destroy();
    }
  },
  validations() {
    return {
      broadbandUrgent: {
        required,
      },
      broadbandDate: {
        required: requiredIf(() => !this.broadbandUrgent),
        minValue: (value) => {
          if (this.broadbandUrgent) return true;

          return (
            new Date(value) >=
            new Date(this.$store.state.houseInformation.startDate)
          );
        },
      },
    };
  },
  filters: {
    FormatDate,
  },
  components: {
    InfoBox,
    FormError,
    CalendarIcon,
  },
};
</script>

<style lang="scss" scoped>
.broadband-form__options-wrapper {
  @include media(">=medium") {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0 (-$spacing-tiny);
  }
}

.installation-option {
  background-color: $white;

  @include media(">=medium") {
    flex-basis: 100%;
    margin-left: $spacing-tiny;
    margin-right: $spacing-tiny;
  }

  @include media("<medium") {
    margin-bottom: $spacing-medium;
  }
}

input[type="radio"] {
  @include visuallyhidden;
}

input[type="radio"] + label {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  min-height: 52px;
  border: 1px solid $grey-mid;
  border-radius: $border-radius-small;
  padding: 10px 10px 10px 60px;

  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: $spacing-small;
    width: 21px;
    height: 21px;
    background-color: transparent;
    border: 1px solid $grey-dark;
    border-radius: 50%;
    box-shadow: inset 0 0 0 6px $white;
    transform: translateY(-50%);
  }
}

input[type="radio"]:checked + label:before {
  background-color: $brand-blue;
}

input[type="radio"]:checked + label,
input[type="radio"]:focus + label {
  border: 3px solid $brand-blue;
}

.installation-option__title {
  font-size: 21px;
  font-weight: $font-regular;
  margin: 0;
}

.broadband-date {
  text-align: center;

  .form__date-input {
    margin: 0 auto;
  }

  label {
    padding: 0;
    margin: $spacing-small 0;
  }
}
</style>
